import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Section from '../components/Section';
import { API, store } from '../api';
import { LOGOS } from '../cdn';
import PromoBanner from '../components/PromoBanner';

const card = 'rounded-2xl border border-[color:var(--color-brand)]/15 bg-[color:var(--color-surface)] p-6';
const fmtGB = (b: number) => (b / 1073741824).toFixed(b < 1073741824 ? 2 : 1) + ' GB';

// Stagger container + item variants — cards fade-up in sequence on mount.
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } } };
const rise = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 0.6, 0.36, 1] as const } } };

/* Interactive card: fade-up in the stagger, lift + brand-glow border on hover.
 * Drop-in for the old `<div className={card}>` blocks. */
function Tile({ children, className = '', span = false }: { children: React.ReactNode; className?: string; span?: boolean }) {
  return (
    <motion.div
      variants={rise}
      whileHover={{ y: -4, transition: { type: 'spring', stiffness: 300, damping: 20 } }}
      className={`${card} ${span ? 'lg:col-span-2' : ''} transition-shadow hover:shadow-[0_18px_50px_-12px_rgba(232,71,106,0.35)] hover:border-[color:var(--color-brand)]/40 ${className}`}
    >
      {children}
    </motion.div>
  );
}

export default function Account() {
  const [authed, setAuthed] = useState(API.loggedIn());
  if (!authed) return <LoginPage onDone={() => setAuthed(true)} />;
  return <Section chip="Account" title="Your account" subtitle="Plan, devices, payments, and support — all in one place."><Portal onLogout={() => { API.logout(); setAuthed(false); }} /></Section>;
}

/* ─────────── Beautified two-column login page. Left = brand pitch,
 * right = form. Collapses to a single column on small screens. Ambient
 * gradient orbs echo the hero without stealing its thunder. */
function LoginPage({ onDone }: { onDone: () => void }) {
  const [reg, setReg] = useState(false);

  return (
    <section className="relative overflow-hidden pt-28 pb-24">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[color:var(--color-brand)]/20 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-40 w-[440px] h-[440px] rounded-full bg-[color:var(--color-brand-deep)]/25 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-5 grid lg:grid-cols-[1fr_460px] gap-10 lg:gap-16 items-center">
        {/* Left — brand + reasons */}
        <div className="text-center lg:text-left">
          <div className="chip mb-5 inline-flex">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand-glow)] animate-pulse" />
            Secure sign-in
          </div>
          <h1 className="h-title text-5xl sm:text-6xl font-extrabold leading-[1.02]">
            Your account,{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--color-brand)] via-[color:var(--color-brand-glow)] to-[color:var(--color-brand-deep)]">
              your rules.
            </span>
          </h1>
          <p className="mt-5 max-w-md mx-auto lg:mx-0 text-[color:var(--color-ink-dim)]">
            Sign in once and manage your plan, devices, and payments in one place. Support chat is one click away.
          </p>

          <ul className="mt-8 grid gap-3 max-w-md mx-auto lg:mx-0 text-sm">
            {[
              ['One tap Google sign-in', 'Handoff token is single-use and expires in 10 minutes.'],
              ['Zero-log by design', 'Only cumulative bytes for plan limits — nothing else.'],
              ['Revoke a device anytime', 'Session tokens are cleared server-side instantly.'],
            ].map(([t, s]) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1 w-5 h-5 rounded-full grid place-items-center bg-[color:var(--color-brand)]/12 text-[color:var(--color-brand-glow)]" aria-hidden>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </span>
                <div><div className="font-semibold text-[color:var(--color-ink)]">{t}</div><div className="text-[color:var(--color-ink-dim)]">{s}</div></div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — form card */}
        <div className={`${card} relative overflow-hidden mx-auto w-full max-w-md`}>
          <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[color:var(--color-brand)]/15 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl ring-1 ring-[color:var(--color-brand)]/30 grid place-items-center overflow-hidden bg-[color:var(--color-bg)]">
                <img src={LOGOS.vpnee} width={36} height={36} alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">{reg ? 'Create account' : 'Welcome back'}</div>
                <div className="h-title text-xl font-extrabold">Sign in to Vpnee</div>
              </div>
            </div>

            {/* Login/register form */}
            <LoginForm reg={reg} onDone={onDone} />

            <div className="flex items-center gap-3 my-5 text-xs text-[color:var(--color-ink-faint)] uppercase tracking-widest">
              <span className="flex-1 h-px bg-[color:var(--color-line)]" />or<span className="flex-1 h-px bg-[color:var(--color-line)]" />
            </div>

            <button className="btn btn-ghost w-full justify-center gap-3 group" onClick={() => { window.location.href = API.socialLoginUrl('google', reg ? 'register' : 'login'); }}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/>
              </svg>
              Continue with Google
            </button>

            <button className="text-sm text-[color:var(--color-ink-dim)] mt-6 w-full text-center hover:text-[color:var(--color-brand-glow)] transition" onClick={() => setReg(!reg)}>
              {reg ? 'Have an account? Sign in' : "Don't have an account? Register"}
            </button>

            <div className="mt-6 pt-4 border-t border-[color:var(--color-line)] text-[11px] text-[color:var(--color-ink-faint)] text-center">
              Protected by HMAC-signed API + one-shot handoff tokens.
            </div>
          </div>
        </div>
      </div>

      <style>{`.input{width:100%;background:rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.08);color:#fff;padding:.85rem 1rem;border-radius:12px;outline:none;font-size:.95rem;transition:border-color .2s,box-shadow .2s}.input:focus{border-color:var(--color-brand);box-shadow:0 0 0 3px rgba(232,71,106,.15)}`}</style>
    </section>
  );
}

function LoginForm({ reg, onDone }: { reg: boolean; onDone: () => void }) {
  const [u, setU] = useState(''); const [e, setE] = useState(''); const [p, setP] = useState('');
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  // OTP step: once register step-1 succeeds we swap the form for a code entry.
  const [otpEmail, setOtpEmail] = useState<string | null>(null);
  const [code, setCode] = useState(''); const [resendMsg, setResendMsg] = useState('');
  const submit = async () => {
    if (!u || !p || (reg && !e)) { setErr('Fill every field.'); return; }
    setBusy(true); setErr('');
    const res = reg ? await API.register(u, e, p) : await API.login(u, p);
    setBusy(false);
    if (res.success) {
      if (reg && (res.data as any)?.otp_required) { setOtpEmail((res.data as any).email || e); setErr(''); return; }
      setErr(''); onDone();
    } else setErr(res.message || 'Failed');
  };
  const verify = async () => {
    if (code.replace(/\D/g, '').length !== 6) { setErr('Enter the 6-digit code.'); return; }
    setBusy(true); setErr('');
    const res = await API.verifyOtp(otpEmail!, code.replace(/\D/g, ''));
    setBusy(false);
    if (res.success) { setErr(''); onDone(); } else setErr(res.message || 'Verification failed');
  };
  const resend = async () => {
    setResendMsg(''); const r = await API.resendOtp(otpEmail!);
    setResendMsg(r.success ? 'New code sent.' : (r.message || 'Could not resend.'));
  };

  if (otpEmail) {
    return (
      <>
        <div className="text-sm text-[color:var(--color-ink-dim)] mb-1">Verify your email</div>
        <div className="text-xs text-[color:var(--color-ink-faint)] mb-3">We sent a 6-digit code to <b className="text-[color:var(--color-ink)]">{otpEmail}</b>.</div>
        <input className="input tracking-[0.5em] text-center text-lg font-mono" placeholder="000000" inputMode="numeric" maxLength={6} value={code}
          onChange={ev => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={ev => ev.key === 'Enter' && verify()} />
        {err && <div className="text-sm text-[color:var(--color-brand-glow)] mt-3">{err}</div>}
        {resendMsg && <div className="text-xs text-[color:var(--color-ok)] mt-2">{resendMsg}</div>}
        <button className="btn btn-primary w-full mt-4 justify-center" disabled={busy} onClick={verify}>{busy ? 'Verifying…' : 'Verify & create account'}</button>
        <div className="flex items-center justify-between mt-3 text-xs">
          <button className="text-[color:var(--color-ink-dim)] hover:text-white" onClick={() => { setOtpEmail(null); setCode(''); setErr(''); }}>← Back</button>
          <button className="text-[color:var(--color-brand-glow)] hover:underline" onClick={resend}>Resend code</button>
        </div>
      </>
    );
  }

  return (
    <>
      <input className="input" placeholder="Username" value={u} autoCapitalize="none" autoComplete="username" onChange={ev => setU(ev.target.value)} />
      {reg && <input className="input mt-3" placeholder="Email" type="email" autoComplete="email" value={e} onChange={ev => setE(ev.target.value)} />}
      <input className="input mt-3" placeholder="Password" type="password" autoComplete={reg ? 'new-password' : 'current-password'} value={p} onChange={ev => setP(ev.target.value)} onKeyDown={ev => ev.key === 'Enter' && submit()} />
      {reg && <div className="text-[11px] text-[color:var(--color-ink-faint)] mt-2">We'll email you a 6-digit code to verify your address.</div>}
      {err && <div className="text-sm text-[color:var(--color-brand-glow)] mt-3">{err}</div>}
      <button className="btn btn-primary w-full mt-4 justify-center" disabled={busy} onClick={submit}>{busy ? (reg ? 'Sending code…' : 'Signing in…') : reg ? 'Continue' : 'Sign in'}</button>
    </>
  );
}

function Portal({ onLogout }: { onLogout: () => void }) {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // Payment-outcome banner. Backend /payment/return + /payment/cancel redirect
  // here with ?paid=1 / ?cancelled=1 so the user sees an answer instead of
  // landing on a silent portal. Also fires on Stripe direct-card success.
  const paid = params.get('paid') === '1';
  const cancelled = params.get('cancelled') === '1';
  const [banner, setBanner] = useState<'paid' | 'cancelled' | null>(paid ? 'paid' : cancelled ? 'cancelled' : null);
  useEffect(() => {
    if (!paid && !cancelled) return;
    // Strip the query so a refresh doesn't re-show the banner. Nav replace so
    // back-button doesn't bring the banner back either.
    const next = new URLSearchParams(params); next.delete('paid'); next.delete('cancelled');
    setParams(next, { replace: true });
    // Auto-dismiss after a comfortable read.
    const t = setTimeout(() => setBanner(null), 6500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [stats, setStats] = useState<any>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  // Discount coupon carried into checkout. free_days coupons never land here —
  // they apply instantly and just trigger a stats reload.
  const [coupon, setCoupon] = useState<{ code: string; pct: number } | null>(null);
  // Site-wide auto-applied promo (server enforces at checkout regardless).
  const [promo, setPromo] = useState<any>(null);
  const load = () => {
    API.stats().then(r => r.success && setStats(r.data));
    API.devices().then(r => r.success && setDevices(r.data || []));
    API.invoices().then(r => r.success && setInvoices(r.data || []));
    API.traffic().then(r => r.success && setLogs(r.data || []));
    API.activePromo().then(setPromo);
  };
  useEffect(load, []);
  // On a fresh paid=1 landing, refresh stats a couple of times so the Pro
  // upgrade shows even if the webhook is a hair behind the browser redirect.
  useEffect(() => {
    if (!paid) return;
    const ts = [1500, 4000, 8000].map(ms => setTimeout(load, ms));
    return () => ts.forEach(clearTimeout);
  }, [paid]);

  const planRaw = String(stats?.plan_type || 'free');
  const isBanned = stats?.is_banned || stats?.ban_status === 'banned';
  const banReason = stats?.ban_reason || '';
  const isPro = stats?.is_pro;
  const plan = planRaw.replace('_', ' ');
  const used = stats?.bytes_used || 0, total = stats?.bytes_total || 0;
  const pct = total ? Math.min(100, Math.max(2, (used / total) * 100)) : 2;

  // Only show tiers ABOVE the current plan — re-listing a plan the user already
  // holds is confusing, not an "upgrade". Mirrors vpnee-cap drawPlans().
  const tier = planRaw.includes('platinum') ? 2 : (planRaw.includes('gold') ? 1 : 0);
  const canGold = tier < 1;
  const canPlatinum = tier < 2;

  const revoke = async (id: string) => { const r = await API.revokeDevice(id); if (r.success) setDevices(d => d.filter(x => x.device_id !== id)); };
  // Route to the dedicated /payments checkout page — receipt, method picker,
  // and token display live there. Backend intent is minted after the user
  // picks a method (no wasted tokens).
  const go = (planKey: string) => {
    const c = coupon?.code ? `&coupon=${encodeURIComponent(coupon.code)}` : '';
    nav(`/payments?plan=${encodeURIComponent(planKey)}${c}`);
  };
  const retry = async (orderId: string) => {
    const res: any = await API.repayIntent(orderId);
    if (res?.url) {
      // Go to SPA checkout with receipt token — shows recomplete UI (PayHere removed)
      const hasToken = res.token ? `&token=${encodeURIComponent(res.token)}` : '';
      const oid = res.order_id || orderId;
      nav(`/payments?order_id=${encodeURIComponent(oid)}${hasToken}&recomplete=1`);
    } else if (typeof res === 'string' && res) {
      window.location.href = res;
    } else {
      alert('Could not reopen this invoice — please try again.');
    }
  };

  const daysLeft = stats?.days_to_reset;
  const resetAt = stats?.reset_at ? String(stats.reset_at).split(' ')[0] : '';
  const pendingDeletion = stats?.pending_deletion;
  const username = stats?.username || (() => { try { return JSON.parse(store.get('vpnee_user') || '{}').username; } catch { return ''; } })();
  const initial = String(username || '?').charAt(0).toUpperCase();

    return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
      {isBanned && (
        <>
          <motion.div variants={rise} className="lg:col-span-2 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
            <span className="mt-0.5 w-7 h-7 rounded-full grid place-items-center bg-red-500/20 text-red-400 shrink-0">⛔</span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-red-300">Account banned</div>
              <div className="text-sm text-red-300/80 mt-0.5">{banReason ? `Reason: ${banReason}` : 'Your access has been revoked for violation of terms.'} VPN disabled — no VLESS served. You can appeal below for justice.</div>
            </div>
          </motion.div>
          <AppealBox />
        </>
      )}
      {banner && (
        <motion.div
          variants={rise}
          className={`lg:col-span-2 rounded-2xl border p-4 flex items-start gap-3 ${
            banner === 'paid'
              ? 'border-[color:var(--color-ok)]/40 bg-[color:var(--color-ok)]/10'
              : 'border-[color:var(--color-brand)]/40 bg-[color:var(--color-brand)]/10'
          }`}
          role="status"
        >
          <span className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center shrink-0 ${banner === 'paid' ? 'bg-[color:var(--color-ok)]/25 text-[color:var(--color-ok)]' : 'bg-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)]'}`}>
            {banner === 'paid' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold">
              {banner === 'paid' ? 'Payment successful' : 'Payment cancelled'}
            </div>
            <div className="text-sm text-[color:var(--color-ink-dim)] mt-0.5">
              {banner === 'paid'
                ? 'Your plan is being activated. If it does not show as Pro within a minute, refresh the page.'
                : 'You cancelled before completing the payment. No charge was made — try again when you\'re ready.'}
            </div>
          </div>
          <button onClick={() => setBanner(null)} className="text-[color:var(--color-ink-faint)] hover:text-white text-lg leading-none px-1" aria-label="Dismiss">✕</button>
        </motion.div>
      )}
      {/* Top bar — identity + prominent sign out (moved up from footer). */}
      <motion.div variants={rise} className="lg:col-span-2 flex items-center justify-between gap-4 rounded-2xl border border-[color:var(--color-brand)]/15 bg-[color:var(--color-surface)] px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl grid place-items-center font-extrabold text-lg text-white shrink-0" style={{ background: 'linear-gradient(135deg,var(--color-brand),var(--color-brand-deep))' }}>{initial}</div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Signed in</div>
            <div className="h-title text-lg font-extrabold truncate">{username || 'Your account'}</div>
            {(() => {
              let prov = 'local'; try { prov = JSON.parse(store.get('vpnee_user') || '{}').auth_provider || 'local'; } catch {}
              const label = prov === 'google' ? 'Google' : prov === 'facebook' ? 'Facebook' : 'Email + password';
              return <div className="text-[11px] text-[color:var(--color-ink-faint)] mt-0.5">Login method: <span className="text-[color:var(--color-ink-dim)]">{label}</span></div>;
            })()}
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
          className="btn btn-ghost gap-2 shrink-0" onClick={onLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          Sign out
        </motion.button>
      </motion.div>

      {/* Global promo banner (site-wide auto-apply) — same unique ticket as Pricing */}
      {promo && (promo.reward_type === 'discount') && (
        <motion.div variants={rise} className="lg:col-span-2">
          <PromoBanner promo={promo} variant="card" />
        </motion.div>
      )}

      {/* Plan — annual only (monthly removed per product decision 2026-08-17) */}
      <Tile>
        {pendingDeletion && (
          <div className="mb-4 rounded-xl border border-[color:var(--color-brand)]/40 bg-[color:var(--color-brand)]/10 px-4 py-3 text-sm">
            <div className="font-semibold text-[color:var(--color-brand-glow)]">Account scheduled for cleanup</div>
            <div className="text-xs text-[color:var(--color-ink-dim)] mt-0.5">Inactive free accounts are removed. Connect or upgrade to keep it.</div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Current plan</div><div className="h-title text-2xl capitalize">{plan}</div></div>
          <span className="chip">{isPro ? 'PRO' : 'FREE'}</span>
        </div>
        <div className="mt-4 h-2 rounded-full bg-white/5 overflow-hidden">
          <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg,var(--color-brand),var(--color-brand-glow))' }} initial={{ width: 0 }} animate={{ width: pct + '%' }} transition={{ duration: 0.9, ease: [0.22, 0.6, 0.36, 1] }} />
        </div>
        <div className="mt-2 text-sm text-[color:var(--color-ink-dim)]">{fmtGB(used)} {total ? 'of ' + fmtGB(total) : 'used'}{stats?.pro_expiry ? ` · expires ${String(stats.pro_expiry).split(' ')[0]}` : ''}</div>
        {typeof daysLeft === 'number' && (
          <div className="mt-1 text-xs text-[color:var(--color-ink-faint)]">
            {daysLeft > 0 ? `Data allowance resets in ${daysLeft} day${daysLeft === 1 ? '' : 's'}` : 'Data allowance resets today'}{resetAt ? ` · ${resetAt}` : ''}
          </div>
        )}
        {isPro && (
          <div className="mt-5 p-3 rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-between gap-3">
            <div className="text-xs"><div className="font-semibold text-[color:var(--color-ink)]">Cancel plan</div><div className="text-[color:var(--color-ink-faint)]">7-day refund if &lt;5% data used</div></div>
            <button onClick={async()=>{
              if(!confirm('Cancel your Pro plan? Refund only within 7 days & <5% usage.')) return;
              let r:any = await API.cancelPlan('');
              // Backend now cancels without refund in one call (200). Old backend returned 400 with eligible_refund:false — handle that gracefully.
              if(!r.success && r.message && (r as any).eligible_refund === false || r.message?.includes('without refund by confirming')){
                const pct = (r as any).pct_used ?? '';
                const msg = pct ? `You've used ${pct}% (over 5% limit). No refund will be issued. Cancel anyway?` : 'No refund eligible. Cancel without refund?';
                if(!confirm(msg)) return;
                r = await API.cancelPlan('confirm_no_refund');
              }
              alert(r.data?.message || r.message || (r.success ? 'Plan cancelled.' : 'Failed'));
              load();
            }} className="text-xs px-3 py-1.5 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10">Cancel</button>
          </div>
        )}
        {(canGold || canPlatinum) ? (
          <>
            <CouponBox coupon={coupon} onDiscount={setCoupon} onRedeemed={load} />
            <div className="mt-4 flex gap-3">
              {canGold && <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="btn btn-primary flex-1" onClick={() => go('gold_annual')}>Gold — {price(20.99, coupon)}</motion.button>}
              {canPlatinum && <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className={`btn ${canGold ? 'btn-ghost' : 'btn-primary'} flex-1`} onClick={() => go('platinum_annual')}>Platinum — {price(31.99, coupon)}</motion.button>}
            </div>
            <div className="mt-3 flex items-center gap-2 justify-center flex-wrap text-xs text-[color:var(--color-ink-faint)]">
              <span className="text-[color:var(--color-ink-dim)]">Pay with:</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/10">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>
                Card
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/10">
                <span aria-hidden style={{ fontFamily: 'monospace', fontWeight: 800, color: '#1cb1e6' }}>支</span>
                Alipay
              </span>
            </div>
            <div className="mt-2 text-xs text-[color:var(--color-ink-faint)] text-center">Annual only. No monthly plans.{coupon ? ` · ${coupon.code} −${coupon.pct}% applied` : (promo && promo.reward_type==='discount' ? ` · ${promo.code} −${promo.reward_value}% auto-applied` : '')}</div>
          </>
        ) : (
          <div className="mt-5 rounded-xl border border-[color:var(--color-brand)]/25 bg-[color:var(--color-brand)]/8 p-4 text-center">
            <div className="h-title text-lg font-extrabold text-[color:var(--color-brand-glow)]">You're on Platinum</div>
            <div className="text-xs text-[color:var(--color-ink-dim)] mt-1">Top tier. Nothing left to unlock.</div>
          </div>
        )}
      </Tile>

      {/* Devices */}
      <Tile>
        <div className="h-title text-lg mb-3">Devices</div>
        {devices.length === 0 && <div className="text-sm text-[color:var(--color-ink-dim)]">No devices registered.</div>}
        {devices.map(d => (
          <div key={d.device_id} className="group flex items-center justify-between py-2.5 border-b border-white/5 last:border-0 transition-colors hover:bg-white/[0.02] rounded-lg px-1">
            <div><div className="text-sm font-semibold">{d.device_brand || 'Device'} · {d.platform || ''}</div><div className="text-xs text-[color:var(--color-ink-faint)]">{d.device_model || ''}</div></div>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.94 }} className="text-xs px-3 py-1.5 rounded-lg border border-[color:var(--color-brand)]/30 text-[color:var(--color-brand-glow)] hover:bg-[color:var(--color-brand)]/10" onClick={() => revoke(d.device_id)}>Revoke</motion.button>
          </div>
        ))}
      </Tile>

      {/* Payments */}
      <Tile>
        <div className="h-title text-lg mb-3">Payment history</div>
        {invoices.length === 0 && <div className="text-sm text-[color:var(--color-ink-dim)]">No payments yet.</div>}
        {invoices.map((inv, i) => {
          const st = String(inv.status || 'pending').toLowerCase();
          const canRetry = (st === 'pending' || st === 'failed') && !!inv.order_id;
          return (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0 text-sm gap-3">
            <div className="min-w-0">
              <div className="font-semibold uppercase">{(inv.plan || 'N/A').replace('_', ' ')}{inv.coupon_code ? <span className="ml-2 normal-case text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--color-brand)]/15 text-[color:var(--color-brand-glow)]">{inv.coupon_code}</span> : null}</div>
              <div className="text-xs text-[color:var(--color-ink-faint)] truncate">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}{inv.order_id ? ` · ${inv.order_id}` : ''}{(inv.receipt_token || inv.token) ? ` · token ${(inv.receipt_token || inv.token).slice(0,8)}` : ''}</div>
            </div>
            <div className="text-right shrink-0">
              <div className={st === 'paid' ? 'text-[color:var(--color-brand-glow)]' : st === 'failed' ? 'text-red-400' : 'text-[color:var(--color-ink-faint)]'}>{st.toUpperCase()}</div>
              <div className="font-semibold">{inv.currency && inv.currency !== 'USD' ? inv.currency + ' ' : '$'}{parseFloat(inv.amount || 0).toFixed(2)}</div>
              {canRetry && <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.94 }} className="mt-1 text-xs px-2.5 py-1 rounded-lg border border-[color:var(--color-brand)]/30 text-[color:var(--color-brand-glow)] hover:bg-[color:var(--color-brand)]/10" onClick={() => retry(inv.order_id)}>Complete payment</motion.button>}
            </div>
          </div>
        );})}
      </Tile>

      {/* Recent activity — connection log from /user/traffic (network_logs). */}
      <Tile>
        <div className="h-title text-lg mb-3">Recent activity</div>
        {logs.length === 0 && <div className="text-sm text-[color:var(--color-ink-dim)]">No recent connection activity. Zero-log plans keep nothing here.</div>}
        <div className="flex flex-col" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {logs.slice(0, 50).map((l, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-sm gap-3">
              <div className="min-w-0"><div className="font-semibold truncate">{l.domain || l.destination_ip || 'connection'}</div>{l.domain && l.destination_ip ? <div className="text-xs text-[color:var(--color-ink-faint)] truncate">{l.destination_ip}</div> : null}</div>
              <div className="text-xs text-[color:var(--color-ink-faint)] shrink-0">{l.timestamp ? new Date(l.timestamp.replace(' ', 'T') + 'Z').toLocaleString() : ''}</div>
            </div>
          ))}
        </div>
      </Tile>

      {/* Support chat */}
      <motion.div variants={rise} className="lg:col-span-2 grid">
        <SupportChat />
      </motion.div>

      {/* Danger zone — self-serve account deletion (required by Google Play + GDPR). */}
      <motion.div variants={rise} className="lg:col-span-2">
        <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-5">
          <div className="flex items-center gap-2 mb-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <div className="text-xs uppercase tracking-widest font-bold text-red-400">Danger zone</div>
          </div>
          <div className="h-title text-lg mb-1">Delete account</div>
          <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed mb-4">
            Permanently removes your account, VPN credentials, devices, invoices, and support history from our primary storage. This action cannot be undone. If you have an active Pro plan, cancel it first from the plan card above.
          </p>
          <button
            className="text-sm px-4 py-2 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition"
            onClick={async () => {
              const conf1 = prompt('This will permanently delete your account and remove all your data. Type DELETE to confirm.');
              if ((conf1 || '').trim().toUpperCase() !== 'DELETE') return;
              const r = await API.deleteAccount();
              if (r.success) {
                alert(r.message || 'Account deleted.');
                onLogout();
              } else {
                alert(r.message || 'Delete failed. Contact support if this persists.');
              }
            }}
          >
            Delete my account
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Render annual price, striking through when a discount coupon is active.
function price(base: number, coupon: { code: string; pct: number } | null) {
  if (!coupon) return `$${base.toFixed(2)}/yr`;
  const disc = base * (1 - coupon.pct / 100);
  return <span>${disc.toFixed(2)}/yr <span className="line-through opacity-60 font-normal">${base.toFixed(2)}</span></span>;
}

// Promo code box. free_days coupons upgrade instantly (reload stats); discount
// coupons lift up to the parent so checkout can carry them.
function CouponBox({ coupon, onDiscount, onRedeemed }: { coupon: { code: string; pct: number } | null; onDiscount: (c: { code: string; pct: number } | null) => void; onRedeemed: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const apply = async () => {
    const c = code.trim().toUpperCase(); if (!c) return;
    setBusy(true); setMsg(null);
    const r = await API.applyCoupon(c);
    setBusy(false);
    if (!r.success) { setMsg({ ok: false, text: r.message || 'Invalid promo code' }); return; }
    const d = r.data || {};
    if (d.reward_type === 'discount') {
      onDiscount({ code: c, pct: Number(d.discount_percentage) || 0 });
      setMsg({ ok: true, text: `Code valid — ${d.discount_percentage}% off applied at checkout.` });
    } else {
      setMsg({ ok: true, text: d.message || 'Promo applied!' });
      setCode(''); onRedeemed();
    }
  };
  if (coupon) return (
    <div className="mt-5 flex items-center justify-between rounded-xl border border-[color:var(--color-brand)]/30 bg-[color:var(--color-brand)]/8 px-3 py-2 text-sm">
      <span><span className="font-semibold text-[color:var(--color-brand-glow)]">{coupon.code}</span> · {coupon.pct}% off</span>
      <button className="text-xs text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-brand-glow)]" onClick={() => onDiscount(null)}>Remove</button>
    </div>
  );
  return (
    <div className="mt-5">
      <div className="flex gap-2">
        <input className="flex-1 bg-black/30 border border-white/8 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[color:var(--color-brand)] uppercase" placeholder="Promo code" value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} />
        <button className="btn btn-ghost" disabled={busy} onClick={apply}>{busy ? '…' : 'Apply'}</button>
      </div>
      {msg && <div className={`mt-2 text-xs ${msg.ok ? 'text-[color:var(--color-brand-glow)]' : 'text-red-400'}`}>{msg.text}</div>}
    </div>
  );
}

function AppealBox() {
  const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false); const [done, setDone] = useState<string|null>(null);
  const send = async () => {
    const m = msg.trim(); if (!m || busy) return; setBusy(true);
    const r = await API.appeal(m); setBusy(false);
    if (r.success) { setDone(r.data?.message || 'Appeal sent — admin will review for justice.'); setMsg(''); }
    else setDone(r.message || 'Failed');
  };
  return (
    <motion.div variants={rise} className="lg:col-span-2 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5">
      <div className="font-bold text-amber-200">Appeal for justice</div>
      <p className="text-xs text-amber-200/70 mt-1">Banned with reason? Tell us your side. Admin reviews in <code>/admin/reviews</code> → Appeals and can unban.</p>
      <textarea value={msg} onChange={e=>setMsg(e.target.value)} rows={3} placeholder="Explain why you should be unbanned…" className="mt-3 w-full rounded-xl bg-black/30 border border-amber-500/20 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500/40" />
      <div className="flex items-center gap-2 mt-3">
        <button disabled={busy || !msg.trim()} onClick={send} className="btn btn-primary !bg-amber-500 hover:!bg-amber-600 disabled:opacity-40">{busy?'Sending…':'Submit appeal'}</button>
        {done && <span className="text-xs text-amber-200">{done}</span>}
      </div>
    </motion.div>
  );
}

function SupportChat() {
  const [msgs, setMsgs] = useState<any[]>([]);
  const [text, setText] = useState('');
  const afterRef = useRef(0);
  const threadRef = useRef<HTMLDivElement | null>(null);

  const pull = async () => {
    const r: any = await API.supportMessages(afterRef.current);
    const list = (r && r.messages) || (r && r.data && r.data.messages) || [];
    if (Array.isArray(list) && list.length) {
      setMsgs(m => [...m, ...list]);
      afterRef.current = Math.max(afterRef.current, ...list.map((x: any) => x.id));
    }
  };
  useEffect(() => { pull(); const id = setInterval(pull, 5000); return () => clearInterval(id); }, []);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    const t = text.trim(); if (!t) return; setText('');
    const r: any = await API.supportSend(t);
    const m = r && (r.message || (r.data && r.data.message));
    if (m) { setMsgs(x => [...x, m]); afterRef.current = Math.max(afterRef.current, m.id); }
  };
  return (
    <div className={`${card} flex flex-col`} style={{ minHeight: 320 }}>
      <div className="h-title text-lg mb-3">Support</div>
      <div ref={threadRef} className="flex-1 overflow-y-auto flex flex-col gap-2 mb-3" style={{ maxHeight: 260 }}>
        {msgs.length === 0 && <div className="text-sm text-[color:var(--color-ink-dim)]">Ask us anything — we usually reply within a day.</div>}
        {msgs.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.25 }}
            className="max-w-[80%] px-3 py-2 rounded-xl text-sm leading-snug break-words"
            style={m.sender === 'user'
              ? { alignSelf: 'flex-end', background: 'linear-gradient(135deg,var(--color-brand),var(--color-brand-deep))', color: '#fff', borderBottomRightRadius: 4 }
              : { alignSelf: 'flex-start', background: 'rgba(255,255,255,0.06)', borderBottomLeftRadius: 4 }}>
            {m.body}
          </motion.div>
        ))}
      </div>
      <div className="flex gap-2">
        <input className="flex-1 bg-black/30 border border-white/8 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[color:var(--color-brand)]" placeholder="Type a message…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} />
        <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }} className="btn btn-primary" onClick={send}>Send</motion.button>
      </div>
    </div>
  );
}
